import React, { useEffect, useMemo } from "react";
import { MapContainer, Marker, useMap } from "react-leaflet";
import Player from "../components/Player";
import { useTheme } from "../contexts/theme";
import { useExperience } from "../contexts/experience";
import { CircularProgress } from "@material-ui/core";
import GpsIcon from '@material-ui/icons/GpsFixedSharp';
import { STADIA_MAPS_API_KEY, PRODUCTION_URL } from "../../config/map/index";
import marker_day from "../../assets/svg/day/marker.svg";
import marker_day_active from "../../assets/svg/day/marker_active.svg";
import marker_night from "../../assets/svg/night/marker.svg";
import marker_night_active from "../../assets/svg/night/marker_active.svg";
import pos_marker_day from "../../assets/svg/day/position.svg";
import pos_marker_night from "../../assets/svg/night/position.svg";
import String from "./String";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.offline";
import Replay from "./Replay";

const BOUNDS_PINETA = [[44.348136, 12.271706],[44.355691, 12.280381]];
const BOUNDS_VERRUCCHIO = [[43.981828, 12.42139],[43.984448, 12.4227669]];
const BOUNDS_ALBERETE = [[44.5121849, 12.213321],[44.516961, 12.222404]];

export default function Map() {

  const { theme } = useTheme();
  const { positions, active, position, visited } = useExperience();

  const mapBounds = useMemo(() => {
    const padding = [0.0001,0.000015];
    let minLat;
    let minLon;
    let maxLat;
    let maxLon;
    positions.forEach((s) => {
      if(!minLat || s.lat < minLat) minLat = s.lat;
      if(!minLon || s.lon < minLon) minLon = s.lon;
      if(!maxLat || s.lat > maxLat) maxLat = s.lat;
      if(!maxLon || s.lon > maxLon) maxLon = s.lon;
    })
    return [[minLat-padding[0],minLon-padding[1]],[maxLat+padding[0],maxLon+padding[1]]];
  }, [positions])

  const pos = position?.coords && [
    position.coords.latitude,
    position.coords.longitude,
  ];

  const markers = {
    day: { default: marker_day, active: marker_day_active },
    night: { default: marker_night, active: marker_night_active },
  };

  const pos_markers = {
    day: pos_marker_day,
    night: pos_marker_night,
  };

  const removeAttribution = (map) => {
    map.attributionControl._container.style = 'display: none';
  }

  const marker = (active) =>
    new L.Icon({
      iconUrl: markers[theme][active ? "active" : "default"],
      popupAnchor: [-0, -0],
      iconSize: [32, 45],
      iconAnchor: [15, 45],
    });
  const position_marker = () =>
    new L.Icon({
      iconUrl: pos_markers[theme],
      iconSize: [16, 16],
    });

  return !pos ? (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <span style={{ display: 'inline-flex', marginBottom: '20px' }}>
        <String s="search_gps" />
        <span><GpsIcon style={{ marginLeft: '8px' }} /></span>
      </span>
      <CircularProgress />
    </div>
  ) : (
    <div style={{ height: "100%", width: "100%" }}>
      {active && (
        <div style={{ width: "100%", zIndex: 2 }}>
          <Player spot={active} />
        </div>
      )}
      {visited && (
        <div style={{ width: "100%", zIndex: 2 }}>
          <Replay spot={visited} />
        </div>
      )}
      <MapContainer
        style={{ height: "100%", zIndex: 0, background: "black" }}
        bounds={mapBounds}
        minZoom={10}
        zoom={50}
        scrollWheelZoom={false}
        zoomControl={false}
        dragging={false}
        doubleClickZoom={false}
        touchZoom={false}
        whenCreated={removeAttribution}
      >
        <Accuracy />
        <Rebound position={position} />

        {theme === "night" ? (
          <>
            <Tile theme="day" />
            <Tile theme="night" />
          </>
        ) : (
          <>
            <Tile theme="night" />
            <Tile theme="day" />
          </>
        )}

        {positions &&
          positions.map((spot) => (
            <Marker
              key={spot.id}
              icon={marker(active && spot.id === active.position)}
              position={[spot.lat, spot.lon]}
            />
          ))}
        {pos && (
          <Marker
            key="position"
            icon={position_marker()}
            position={pos}
            zIndexOffset={999}
          />
        )}
      </MapContainer>
    </div>
  );
}

function Tile({ theme }) {
  const map = useMap();
  useEffect(() => {
    const tiles = {
      day: {
        attribution:
          '&copy; <a href="https://stadiamaps.com/">Stadia Maps</a>, &copy; <a href="https://openmaptiles.org/">OpenMapTiles</a> &copy; <a href="http://openstreetmap.org">OpenStreetMap</a> contributors',
        url: `https://tiles.stadiamaps.com/tiles/alidade_smooth/{z}/{x}/{y}{r}.png?api_key=${STADIA_MAPS_API_KEY}`,
      },
      night: {
        attribution:
          '&copy; <a href="https://stadiamaps.com/">Stadia Maps</a>, &copy; <a href="https://openmaptiles.org/">OpenMapTiles</a> &copy; <a href="http://openstreetmap.org">OpenStreetMap</a> contributors',
        url: `https://tiles.stadiamaps.com/tiles/alidade_smooth_dark/{z}/{x}/{y}{r}.png?api_key=${STADIA_MAPS_API_KEY}`,
      },
    };
    const tile = tiles[theme];
    const offline = L.tileLayer
      .offline(tile.url, {
        attribution: tile.attribution,
        subdomains: PRODUCTION_URL,
        minZoom: 10,
      })
      .addTo(map);
    return () => offline.removeFrom(map);
  }, [theme, map]);
  return null;
}

function Accuracy() {
  const { position } = useExperience();
  const { theme } = useTheme();
  const accuracy = position?.coords?.accuracy;
  const style = {
    position: "absolute",
    bottom: "0px",
    color: theme === "day" ? "#000" : "#fff",
    padding: "7px 15px",
    zIndex: 999,
    opacity: 0.7,
    fontSize: "10px",
  };
  return accuracy ? (
    <span style={style}>
      <strong>Precisione GPS: </strong>
      {accuracy.toFixed(2)}m
    </span>
  ) : null;
}

function Rebound({position}) {
  const map = useMap();

  useEffect(() => {
    if (position && position.coords && position.coords.accuracy < 1000) {

      const padding = [0.1, 0.1];

      // pineta
      const pineta = [
        [BOUNDS_PINETA[0][0]-padding[0],BOUNDS_PINETA[0][1]-padding[1]],
        [BOUNDS_PINETA[1][0]+padding[0],BOUNDS_PINETA[1][1]+padding[1]]
      ];
      if (L.latLngBounds(pineta).contains([
        position.coords.latitude, position.coords.longitude
      ])) map.fitBounds(BOUNDS_PINETA);

      // verrucchio
      const verrucchio = [
        [BOUNDS_VERRUCCHIO[0][0]-padding[0],BOUNDS_VERRUCCHIO[0][1]-padding[1]],
        [BOUNDS_VERRUCCHIO[1][0]+padding[0],BOUNDS_VERRUCCHIO[1][1]+padding[1]]
      ];
      if (L.latLngBounds(verrucchio).contains([
        position.coords.latitude, position.coords.longitude
      ])) map.fitBounds(BOUNDS_VERRUCCHIO);

      // alberete
      const alberete = [
        [BOUNDS_ALBERETE[0][0]-padding[0],BOUNDS_ALBERETE[0][1]-padding[1]],
        [BOUNDS_ALBERETE[1][0]+padding[0],BOUNDS_ALBERETE[1][1]+padding[1]]
      ];
      if (L.latLngBounds(alberete).contains([
        position.coords.latitude, position.coords.longitude
      ])) map.fitBounds(BOUNDS_ALBERETE);
    }
  }, [position, map])

  return null
}
